import useApiRequests from "@services/http/useApiRequest";
import PanelWithTable, { PanelTableRow } from "./PanelWithTable";
import { SegmentedValue } from "antd/es/segmented";
import { useIntl } from "react-intl";
import { useCallback, useEffect, useState } from "react";
import useTypedRequest, { castResponse } from "@utils/hooks/useTypedRequest";
import { deleteMinerErrors, IMinerErrors } from "@services/minerErrors";
import moment from "moment";
import { PostRequest } from "@services/http/BaseRequest";
import { Button, Pagination } from "antd";
import { useMinerContext } from "../../MinerContext";
import { MinerActions } from "../../minerReducer";
import { Segments } from "./MinerRowMoreService";
import W2UiFilterBuilder from "@services/w2ui";
import { SearchItem } from "@services/w2ui/w2const";
import { RecordsResponse } from "wl-net";

const PAGE_SIZE = 20;

const GetMinerErrorsList = {
  makeRequest(id: number, page: number) {
    const params = new W2UiFilterBuilder()
      .setOffset((page - 1) * PAGE_SIZE)
      .setLimit(PAGE_SIZE)
      .setSearch([SearchItem.intIs("id", id)])
      .build()
      .stringParams;
    return new PostRequest(
      "/admin/control/errors/list",
      params
    );
  },
  makeResponse: castResponse<RecordsResponse<IMinerErrors>>,
}

const GetMinerHistoryErrors = {
  makeRequest(id: number, page: number) {
    const params = new W2UiFilterBuilder()
      .setOffset((page - 1) * PAGE_SIZE)
      .setLimit(PAGE_SIZE)
      .setSearch([SearchItem.intIs("id", id)])
      .build()
      .stringParams;
    return new PostRequest(
      "/admin/control/errors/history/list",
      params
    );
  },
  makeResponse: castResponse<RecordsResponse<IMinerErrors>>,
};

interface ParsedMinerErrorData {
  cause?: string;
  code?: string | number;
  suggestion?: string;
}

const parseMinerErrorData = (data: string): ParsedMinerErrorData => {
  try {
    const parsed = JSON.parse(data || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const record = parsed as Record<string, unknown>;
    const result: ParsedMinerErrorData = {};
    if (typeof record.code === "string" || typeof record.code === "number") {
      result.code = record.code;
    }
    if (typeof record.cause === "string") {
      result.cause = record.cause;
    }
    if (typeof record.suggestion === "string") {
      result.suggestion = record.suggestion;
    }
    return result;
  } catch {
    return {};
  }
};

function useControl(
  minerId: number
) {

  const [isLoading, setLoading] = useState(false);
  const { request: getErrors } = useTypedRequest(GetMinerErrorsList); //getMinerErrors);
  const { request: getErrorsHistory } = useTypedRequest(GetMinerHistoryErrors);
  const { request: clearMinerErrors } = useTypedRequest(deleteMinerErrors);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [currentSegment, setCurrentSegment] = useState<Segments>(Segments.CURRENT);
  const [rows, setRows] = useState<PanelTableRow[]>([]);
  const { dispatchMiner } = useMinerContext();

  //const requests = useApiRequests();

  const onSegmentChanged = (value: SegmentedValue) => {
    if (value === Segments.CURRENT) {
      setCurrentSegment(Segments.CURRENT);
    }
    if (value === Segments.HISTORY) {
      setCurrentSegment(Segments.HISTORY);
    }
  }

  const updateRows = useCallback(async (segment: Segments) => {
    const handlers = {
      [Segments.CURRENT]: () => getErrors(minerId, page),
      [Segments.HISTORY]: () => getErrorsHistory(minerId, page),
    };
    setLoading(true);
    try {

      const handler = handlers[segment];
      if (!handler) {
        throw new Error("Unknown segment");
      }
      const response = await handler();
      const newRows: PanelTableRow[] = [];
      if (response && response.Status === "success") {
        setTotal(response.Total);
        response.Records.forEach(e => {
          const parsed = parseMinerErrorData(e.Data);
          if (parsed) {
            const row: PanelTableRow = {
              values: [
                parsed.code ?? "",
                parsed.cause ?? "",
                parsed.suggestion ?? "",
                moment(e.Timestamp * 1000).format("DD.MM.YYYY HH:mm:ss")
              ]
            }
            newRows.push(row);
          }
        });
      }
      setRows(newRows);
    } finally {
      setLoading(false);
    }
  }, [minerId, page]);

  const clearErrorsHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clearMinerErrors(minerId);
      if (response && response.Status === "success") {
        setRows([]);
        dispatchMiner({
          type: MinerActions.ResetHasErrors,
          payload: {},
        });
      }
    }
    finally {
      setLoading(false);
    }
  }, [minerId]);

  const onPageChange = (page: number, pageSize: number) => {
    setPage(page);
  }

  useEffect(() => {
    updateRows(currentSegment);
  }, [currentSegment]);

  return {
    page,
    total,
    currentSegment,
    isLoading,
    rows,
    onSegmentChanged,
    clearErrorsHistory,
    onPageChange,
  }
}

// class AddFake extends PostRequest {
//   constructor(minerId: number) {
//     super(`admin/control/errors/add_fake?who=controller&miner_id=${minerId}`, {});
//   }
// }

function MinerErrorsHistory({ minerId }: { minerId: number }): JSX.Element {

  const { formatMessage } = useIntl();

  const control = useControl(minerId);

  ///// fake
  // const requests = useApiRequests();
  // const addFake = async () => {
  //   const response = await requests.enqueueSilent(new AddFake(minerId));
  //   console.log("AddFake response", response);
  //   // api/admin/control/errors/add_fake?miner_id=837&who=controller
  // }
  /// end fake

  return (<>
    {/* <Button onClick={addFake}>Add Fake</Button> */}
    <PanelWithTable
      caption="Ошибки"
      isLoading={control.isLoading}
      segments={[
        { label: "текущие", value: Segments.CURRENT },
        { label: "история", value: Segments.HISTORY }
      ]}
      onSegmentChanged={control.onSegmentChanged}
      columns={[
        // Код
        // Тип
        // Пояснения
        // Дата
        formatMessage({ id: "machines.more.settings.miner_errors.col-1" }),
        formatMessage({ id: "machines.more.settings.miner_errors.col-2" }),
        formatMessage({ id: "machines.more.settings.miner_errors.col-3" }),
        formatMessage({ id: "machines.more.settings.miner_errors.col-4" }),
      ]}
      rows={control.rows}
      additional={control.currentSegment === Segments.CURRENT && (
        <Button size="small" type="text" onClick={control.clearErrorsHistory}>
          Очистить
        </Button>
      )}
    />
    <Pagination size="small"
      defaultCurrent={1}
      current={control.page}
      total={control.total}
      pageSize={PAGE_SIZE}
      onChange={control.onPageChange}
    />
  </>);
}

export default MinerErrorsHistory;